import { useEffect, useState } from 'react';
import { api, resolveImageUrl } from '../lib/api.js';

interface ProductData {
  asin: string;
  domainId: number;
  competesWith: string | null;
  title: string | null;
  brand: string | null;
  imageUrl: string | null;
  salesRank: number | null;
  monthlySold: number | null;
  priceBuyBox: number | null;
  priceAmazon: number | null;
  priceNew: number | null;
  rating: number | null;
  reviewCount: number | null;
  offerCount: number | null;
  bsr30: number | null;
  bsr90: number | null;
}

interface CompetitiveData {
  own: ProductData[];
  competitors: ProductData[];
  bsrHistory: Array<{ asin: string; domain_id: number; date: string; sales_rank: number; monthly_sold: number | null }>;
}

interface Account { id: string; name: string }

const DOMAIN_LABELS: Record<number, string> = { 1: 'US', 2: 'UK', 3: 'DE', 4: 'FR', 6: 'CA', 8: 'IT', 9: 'ES' };
const DOMAIN_URL: Record<number, string> = { 1: 'amazon.com', 2: 'amazon.co.uk', 3: 'amazon.de', 6: 'amazon.ca' };

function fmtPrice(cents: number | null): string {
  if (cents == null || cents < 0) return '\u2014';
  return `$${cents.toFixed(2)}`;
}

function fmtBsr(rank: number | null): string {
  if (rank == null || rank < 0) return '\u2014';
  return `#${rank.toLocaleString()}`;
}

export function CompetitiveIntel() {
  const [allData, setAllData] = useState<CompetitiveData | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<number | null>(null);
  const [expandedAsin, setExpandedAsin] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<Account[]>('/accounts').then(async accts => {
      if (accts.length === 0) { setLoading(false); return; }
      const results = await Promise.all(
        accts.map(a => api.get<CompetitiveData>(`/keepa/competitive/${a.id}`).catch(() => ({ own: [], competitors: [], bsrHistory: [] }))),
      );
      const merged: CompetitiveData = { own: [], competitors: [], bsrHistory: [] };
      for (const r of results) {
        merged.own.push(...r.own);
        merged.competitors.push(...r.competitors);
        merged.bsrHistory.push(...r.bsrHistory);
      }
      const dedup = (arr: ProductData[]) => {
        const seen = new Set<string>();
        return arr.filter(p => { const k = `${p.asin}:${p.domainId}`; if (seen.has(k)) return false; seen.add(k); return true; });
      };
      merged.own = dedup(merged.own);
      merged.competitors = dedup(merged.competitors);
      setAllData(merged);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton />;

  const allDomains = new Set<number>();
  for (const p of [...(allData?.own ?? []), ...(allData?.competitors ?? [])]) {
    if (p.domainId) allDomains.add(p.domainId);
  }
  const domains = [...allDomains].sort();

  const data: CompetitiveData = {
    own: (allData?.own ?? []).filter(p => !selectedDomain || p.domainId === selectedDomain),
    competitors: (allData?.competitors ?? []).filter(p => !selectedDomain || p.domainId === selectedDomain),
    bsrHistory: (allData?.bsrHistory ?? []).filter(h => !selectedDomain || h.domain_id === selectedDomain),
  };

  // Group own products by ASIN (across markets)
  const ownByAsin = new Map<string, ProductData[]>();
  for (const p of data.own) {
    if (!ownByAsin.has(p.asin)) ownByAsin.set(p.asin, []);
    ownByAsin.get(p.asin)!.push(p);
  }

  // Group competitors by competesWith ASIN
  const compByOwn = new Map<string, ProductData[]>();
  for (const c of data.competitors) {
    const key = c.competesWith ?? '_unlinked';
    if (!compByOwn.has(key)) compByOwn.set(key, []);
    compByOwn.get(key)!.push(c);
  }

  const ownAsins = [...ownByAsin.keys()];
  const totalComps = data.competitors.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Competitive Intelligence</h2>
          <p className="text-sm text-gray-500">Click a product to see its competitors</p>
        </div>
      </div>

      {/* Market selector */}
      {domains.length > 1 && (
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedDomain(null)}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
              !selectedDomain ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >All Markets</button>
          {domains.map(d => (
            <button key={d} onClick={() => setSelectedDomain(d === selectedDomain ? null : d)}
              className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                selectedDomain === d ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >{DOMAIN_LABELS[d] ?? `Domain ${d}`}</button>
          ))}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard label="Your Products" value={String(ownAsins.length)} />
        <KpiCard label="Competitors Tracked" value={String(totalComps)} />
        <KpiCard label="Markets" value={String(domains.length)} />
      </div>

      {/* Products with expandable competitors */}
      {ownAsins.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-400">
          No own products tracked. Add ASINs in Keepa Tracking.
        </div>
      ) : (
        <div className="space-y-3">
          {ownAsins.map(asin => {
            const variants = ownByAsin.get(asin)!;
            const primary = variants[0]!;
            const comps = compByOwn.get(asin) ?? [];
            const isExpanded = expandedAsin === asin;

            return (
              <div key={asin} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                {/* Own product header — clickable */}
                <button
                  onClick={() => setExpandedAsin(isExpanded ? null : asin)}
                  className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {primary.imageUrl ? (
                      <img src={resolveImageUrl(primary.imageUrl) ?? ''} alt="" className="h-14 w-14 rounded-lg border border-gray-100 object-contain flex-shrink-0" loading="lazy" />
                    ) : (
                      <div className="h-14 w-14 rounded-lg border border-gray-100 bg-gray-50 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">{primary.title ?? primary.brand ?? asin}</span>
                        <span className="text-xs text-gray-400">{asin}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        {variants.map(v => (
                          <span key={v.domainId} className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600">{DOMAIN_LABELS[v.domainId]}</span>
                            BSR {fmtBsr(v.salesRank)}
                            {v.monthlySold != null && <span className="text-gray-400">({v.monthlySold}/mo)</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">{fmtPrice(primary.priceBuyBox)}</p>
                        <p className="text-xs text-gray-400">{primary.rating ? `${primary.rating.toFixed(1)} (${primary.reviewCount ?? 0})` : ''}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${comps.length > 0 ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                          {comps.length} comp{comps.length !== 1 ? 's' : ''}
                        </span>
                        <svg className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Expanded competitors */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50/50">
                    {comps.length === 0 ? (
                      <p className="px-5 py-4 text-sm text-gray-400">No competitors linked to this product yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-100">
                              <th className="px-5 py-2 text-left text-xs font-medium text-gray-500">Competitor</th>
                              <th className="px-2 py-2 text-center text-xs font-medium text-gray-500">Mkt</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">BSR</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">Monthly Sold</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">Price</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">Rating</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">Reviews</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">Sellers</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {/* Own product row for comparison */}
                            {variants.map(v => (
                              <tr key={`own-${v.domainId}`} className="bg-brand-50/30">
                                <td className="px-5 py-2">
                                  <div className="flex items-center gap-2">
                                    <span className="rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-bold text-brand-700">YOU</span>
                                    <span className="text-xs font-medium text-gray-700">{v.title ?? v.brand ?? asin}</span>
                                  </div>
                                </td>
                                <td className="px-2 py-2 text-center"><span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">{DOMAIN_LABELS[v.domainId]}</span></td>
                                <td className="px-2 py-2 text-right tabular-nums font-semibold text-brand-600">{fmtBsr(v.salesRank)}</td>
                                <td className="px-2 py-2 text-right tabular-nums font-semibold text-brand-600">{v.monthlySold ?? '\u2014'}</td>
                                <td className="px-2 py-2 text-right tabular-nums font-semibold text-brand-600">{fmtPrice(v.priceBuyBox)}</td>
                                <td className="px-2 py-2 text-right tabular-nums font-semibold text-brand-600">{v.rating ? v.rating.toFixed(1) : '\u2014'}</td>
                                <td className="px-2 py-2 text-right tabular-nums font-semibold text-brand-600">{v.reviewCount ?? '\u2014'}</td>
                                <td className="px-2 py-2 text-right tabular-nums font-semibold text-brand-600">{v.offerCount ?? '\u2014'}</td>
                              </tr>
                            ))}
                            {/* Competitor rows */}
                            {comps.sort((a, b) => (a.salesRank ?? 999999) - (b.salesRank ?? 999999)).map(c => {
                              const bsrBetter = primary.salesRank && c.salesRank && c.salesRank < primary.salesRank;
                              const priceLower = primary.priceBuyBox && c.priceBuyBox && c.priceBuyBox < primary.priceBuyBox;
                              return (
                                <tr key={`${c.asin}-${c.domainId}`} className="hover:bg-white">
                                  <td className="px-5 py-2">
                                    <div className="flex items-center gap-2">
                                      {c.imageUrl ? (
                                        <img src={resolveImageUrl(c.imageUrl) ?? ''} alt="" className="h-8 w-8 rounded border border-gray-100 object-contain flex-shrink-0" loading="lazy" />
                                      ) : (
                                        <div className="h-8 w-8 rounded border border-gray-100 bg-gray-50 flex-shrink-0" />
                                      )}
                                      <div className="min-w-0">
                                        <a href={`https://www.${DOMAIN_URL[c.domainId] ?? 'amazon.com'}/dp/${c.asin}`}
                                          target="_blank" rel="noopener noreferrer"
                                          className="text-xs font-medium text-brand-600 hover:underline">{c.asin}</a>
                                        <p className="text-[10px] text-gray-400 truncate max-w-[280px]">{c.title ?? c.brand ?? ''}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-2 py-2 text-center"><span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">{DOMAIN_LABELS[c.domainId]}</span></td>
                                  <td className={`px-2 py-2 text-right tabular-nums font-medium ${bsrBetter ? 'text-red-500' : 'text-gray-700'}`}>{fmtBsr(c.salesRank)}</td>
                                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">{c.monthlySold ?? '\u2014'}</td>
                                  <td className={`px-2 py-2 text-right tabular-nums font-medium ${priceLower ? 'text-red-500' : 'text-gray-700'}`}>{fmtPrice(c.priceBuyBox)}</td>
                                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">{c.rating ? c.rating.toFixed(1) : '\u2014'}</td>
                                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">{c.reviewCount ?? '\u2014'}</td>
                                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">{c.offerCount ?? '\u2014'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Unlinked competitors */}
          {(compByOwn.get('_unlinked')?.length ?? 0) > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h3 className="text-sm font-semibold mb-3 text-gray-500">Unlinked Competitors</h3>
              <p className="text-xs text-gray-400 mb-3">These competitors haven't been linked to any of your products yet.</p>
              <CompactTable products={compByOwn.get('_unlinked')!} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompactTable({ products }: { products: ProductData[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="pb-2 text-left text-xs font-medium text-gray-500">Product</th>
            <th className="pb-2 text-center text-xs font-medium text-gray-500">Mkt</th>
            <th className="pb-2 text-right text-xs font-medium text-gray-500">BSR</th>
            <th className="pb-2 text-right text-xs font-medium text-gray-500">Price</th>
            <th className="pb-2 text-right text-xs font-medium text-gray-500">Rating</th>
            <th className="pb-2 text-right text-xs font-medium text-gray-500">Reviews</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {products.map(p => (
            <tr key={`${p.asin}-${p.domainId}`} className="hover:bg-gray-50">
              <td className="py-2">
                <a href={`https://www.${DOMAIN_URL[p.domainId] ?? 'amazon.com'}/dp/${p.asin}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-xs font-medium text-brand-600 hover:underline">{p.asin}</a>
                <p className="text-[10px] text-gray-400 truncate max-w-[250px]">{p.title ?? p.brand ?? ''}</p>
              </td>
              <td className="py-2 text-center"><span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">{DOMAIN_LABELS[p.domainId]}</span></td>
              <td className="py-2 text-right tabular-nums">{fmtBsr(p.salesRank)}</td>
              <td className="py-2 text-right tabular-nums">{fmtPrice(p.priceBuyBox)}</td>
              <td className="py-2 text-right tabular-nums">{p.rating ? p.rating.toFixed(1) : '\u2014'}</td>
              <td className="py-2 text-right tabular-nums">{p.reviewCount ?? '\u2014'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-5 w-48 rounded bg-gray-200" />
      <div className="grid gap-4 sm:grid-cols-3">
        {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-xl bg-gray-200" />)}
      </div>
      <div className="h-24 rounded-xl bg-gray-200" />
      <div className="h-24 rounded-xl bg-gray-200" />
    </div>
  );
}
